import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useHistory } from 'react-router-dom';

import { Grid, List, ListItem, ListItemText } from '@material-ui/core';

import MenuIcon from '@material-ui/icons/Menu';

import { ThemeNestedMenu, ThemeLoader } from 'src/theming';
import { useAbilityCtx } from 'src/roleAccesses';

import { useHttpClient } from 'src/lib/http-client/use-http-client';

import {
    useStyle,
    useContractDetailsViewDataStyle,
} from '../contracts-details-view-styles';

import { ContractDetailsListItem } from '../contract-details-list-item';

import { generateContractsDetailsAdditionalMenu } from '../contract-details-view-configs';

import { ContractDetailsViewTranspResponseBody } from './contract-details-view-transp-response-body';

interface ContractDetailsViewTranspDetailsProps {
    handleOpenContractEdit: () => void;
    hideThemeNestedMenu: boolean;
    contractNumber: string;
}
export const ContractDetailsViewTranspDetails = (
    props: ContractDetailsViewTranspDetailsProps
): JSX.Element => {
    const { t } = useTranslation(['contract-details-view']);

    const ability = useAbilityCtx();
    const classes = useStyle();
    const contractDetailsViewDataClasses = useContractDetailsViewDataStyle();

    const { handleOpenContractEdit, hideThemeNestedMenu, contractNumber } = props;

    const router = useHistory();
    const { id } = useParams<{ id: string }>();
    const httpClient = useHttpClient();
    const [isLoadingDetails, setLoadingDetails] = useState(false);
    const [detailsData, setDetailsData] =
        useState<ContractDetailsViewTranspResponseBody>();
    const makeRequestForDetails =
        async (): Promise<ContractDetailsViewTranspResponseBody> => {
            setLoadingDetails(true);
            return httpClient.get<ContractDetailsViewTranspResponseBody>(
                `contracts/${id}/transp`
            );
        };
    useEffect(() => {
        makeRequestForDetails()
            .then((data: ContractDetailsViewTranspResponseBody) => {
                setDetailsData(data);
            })
            .finally(() => {
                setLoadingDetails(false);
            });
    }, []);

    const goTo = (pathname: string): void => {
        router.push(pathname);
    };

    const menuItems = useMemo(
        () =>
            generateContractsDetailsAdditionalMenu(
                t,
                null,
                handleOpenContractEdit,
                goTo,
                ability,
                id,
                contractNumber
            ),
        [t, contractNumber, ability]
    );

    const isDetailDataEmpty = useMemo(() => {
        if (detailsData) {
            const detailsDataKeys = Object.keys(detailsData);
            return !detailsDataKeys?.map((x) => detailsData[x])?.filter((x) => x)?.length;
        }
        return true;
    }, [detailsData]);
    return (
        <Grid container>
            <Grid container className={classes.titleAndMenuContainer}>
                <div style={{ flexGrow: 1 }} />
                {!hideThemeNestedMenu &&
                    Array.isArray(menuItems) &&
                    menuItems.length > 0 && (
                        <ThemeNestedMenu
                            menuItems={menuItems}
                            buttonClass={classes.additionalMenuButton}
                            buttonIcon={<MenuIcon className={classes.menuIcon} />}
                        />
                    )}
            </Grid>
            {isLoadingDetails && <ThemeLoader />}
            {isDetailDataEmpty && (
                <ListItem>
                    <ListItemText data-title>
                        {t('contract-details-view:noContractDetails')}
                    </ListItemText>
                </ListItem>
            )}
            {detailsData && !isLoadingDetails && (
                <>
                    <List className={contractDetailsViewDataClasses.list}>
                        <ContractDetailsListItem
                            value={detailsData?.insuranceRisk}
                            title='contract-details-view:contractDetails.versRisiko'
                            hidden={!detailsData?.insuranceRisk}
                        />
                        <ContractDetailsListItem
                            value={t('formatted-values:formattedCurrency', {
                                currency: detailsData?.insuranceSumInEURO,
                            })}
                            title='contract-details-view:contractDetails.insuranceSumInEuro'
                            hidden={
                                detailsData?.insuranceSumInEURO === null ||
                                detailsData?.insuranceSumInEURO === 0
                            }
                        />
                        <ContractDetailsListItem
                            value={
                                detailsData?.deductibleInPercent
                                    ? String(detailsData?.deductibleInPercent)
                                    : ''
                            }
                            title='contract-details-view:contractDetails.deductibleInPercent'
                            hidden={
                                detailsData?.deductibleInPercent === null ||
                                detailsData?.deductibleInPercent === 0
                            }
                        />
                        <ContractDetailsListItem
                            value={detailsData?.remark}
                            title='contract-details-view:contractDetails.remark'
                            hidden={!detailsData?.remark}
                        />
                    </List>
                </>
            )}
        </Grid>
    );
};
